import React, { createContext, useCallback, useContext, useMemo, useReducer } from 'react';
import type { ReactNode } from 'react';

import {
  AzureRepoIntegrationPayload,
  CreateProjectPayload,
  DomainKnowledge,
  EnterpriseKnowledge,
  EnterpriseKnowledgePayload,
  ProjectWithRepository,
  RepositorySummary,
  UpdateProjectPayload,
} from '../types/project';
import {
  QueueRequirementGenerationPayload,
  RequirementDocument,
  RequirementGenerationJob,
} from '../types/requirements';
import * as ProjectService from '../api/projects';
import type { CreateProjectWithKnowledgeOptions } from '../api/projects';
import * as RequirementService from '../api/requirements';

interface State {
  projects: ProjectWithRepository[];
  selectedProject: ProjectWithRepository | null;
  loading: boolean;
  error: string | null;
  requirementDocuments: Record<number, RequirementDocument[]>;
  requirementJobs: Record<number, RequirementGenerationJob[]>;
  domainKnowledge: Record<number, DomainKnowledge | null>;
  enterpriseKnowledge: Record<number, EnterpriseKnowledge | null>;
}

const initialState: State = {
  projects: [],
  selectedProject: null,
  loading: false,
  error: null,
  requirementDocuments: {},
  requirementJobs: {},
  domainKnowledge: {},
  enterpriseKnowledge: {},
};

const extractErrorMessage = (error: any): string => {
  return error?.response?.data?.detail || error?.message || 'Operation failed';
};

type Action =
  | { type: 'SET_PROJECTS'; payload: ProjectWithRepository[] }
  | { type: 'UPSERT_PROJECT'; payload: ProjectWithRepository }
  | { type: 'REMOVE_PROJECT'; payload: number }
  | { type: 'SET_SELECTED'; payload: ProjectWithRepository | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_REQUIREMENT_DOCUMENTS'; payload: { projectId: number; documents: RequirementDocument[] } }
  | { type: 'SET_REQUIREMENT_JOBS'; payload: { projectId: number; jobs: RequirementGenerationJob[] } }
  | { type: 'SET_DOMAIN_KNOWLEDGE'; payload: { projectId: number; knowledge: DomainKnowledge | null } }
  | { type: 'SET_ENTERPRISE_KNOWLEDGE'; payload: { projectId: number; knowledge: EnterpriseKnowledge | null } };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_PROJECTS':
      return { ...state, projects: action.payload };
    case 'UPSERT_PROJECT': {
      const existingIndex = state.projects.findIndex(p => p.id === action.payload.id);
      const projects = [...state.projects];
      if (existingIndex >= 0) {
        projects[existingIndex] = action.payload;
      } else {
        projects.unshift(action.payload);
      }
      const domainKnowledge = { ...state.domainKnowledge };
      const enterpriseKnowledge = { ...state.enterpriseKnowledge };
      if ((action.payload as ProjectWithRepository).domain_knowledge !== undefined) {
        domainKnowledge[action.payload.id] = action.payload.domain_knowledge ?? null;
      }
      if ((action.payload as ProjectWithRepository).enterprise_knowledge !== undefined) {
        enterpriseKnowledge[action.payload.id] = action.payload.enterprise_knowledge ?? null;
      }
      return { ...state, projects, selectedProject: action.payload, domainKnowledge, enterpriseKnowledge };
    }
    case 'REMOVE_PROJECT': {
      const { [action.payload]: _discarded, ...remainingKnowledge } = state.domainKnowledge;
      const { [action.payload]: _discardedEnterprise, ...remainingEnterpriseKnowledge } = state.enterpriseKnowledge;
      return {
        ...state,
        projects: state.projects.filter(p => p.id !== action.payload),
        selectedProject: state.selectedProject?.id === action.payload ? null : state.selectedProject,
        domainKnowledge: remainingKnowledge,
        enterpriseKnowledge: remainingEnterpriseKnowledge,
      };
    }
    case 'SET_SELECTED':
      return { ...state, selectedProject: action.payload };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_REQUIREMENT_DOCUMENTS':
      return {
        ...state,
        requirementDocuments: {
          ...state.requirementDocuments,
          [action.payload.projectId]: action.payload.documents,
        },
      };
    case 'SET_REQUIREMENT_JOBS':
      return {
        ...state,
        requirementJobs: {
          ...state.requirementJobs,
          [action.payload.projectId]: action.payload.jobs,
        },
      };
    case 'SET_DOMAIN_KNOWLEDGE':
      return {
        ...state,
        domainKnowledge: {
          ...state.domainKnowledge,
          [action.payload.projectId]: action.payload.knowledge,
        },
      };
    case 'SET_ENTERPRISE_KNOWLEDGE':
      return {
        ...state,
        enterpriseKnowledge: {
          ...state.enterpriseKnowledge,
          [action.payload.projectId]: action.payload.knowledge,
        },
      };
    default:
      return state;
  }
};

interface ProjectContextValue extends State {
  refreshProjects: () => Promise<void>;
  refreshProject: (projectId: number) => Promise<ProjectWithRepository | null>;
  createProject: (payload: CreateProjectPayload) => Promise<ProjectWithRepository>;
  createProjectWithKnowledge: (
    payload: CreateProjectPayload,
    options?: CreateProjectWithKnowledgeOptions,
  ) => Promise<ProjectWithRepository>;
  updateProject: (projectId: number, payload: UpdateProjectPayload) => Promise<ProjectWithRepository>;
  deleteProject: (projectId: number) => Promise<void>;
  uploadRepository: (
    projectId: number,
    file: File,
    metadata?: { name?: string; description?: string; azureRepoName?: string; azureBranch?: string },
  ) => Promise<RepositorySummary>;
  integrateAzureRepo: (
    projectId: number,
    payload: AzureRepoIntegrationPayload,
  ) => Promise<RepositorySummary>;
  removeRepository: (projectId: number) => Promise<void>;
  fetchRequirementDocuments: (projectId: number) => Promise<RequirementDocument[]>;
  uploadRequirementDocuments: (
    projectId: number,
    files: File[],
    options?: { notes?: string; classification?: import('../types/requirements').DocumentClassification },
  ) => Promise<RequirementDocument[]>;
  deleteRequirementDocument: (projectId: number, documentId: number) => Promise<void>;
  reprocessRequirementDocument: (projectId: number, documentId: number) => Promise<RequirementDocument>;
  downloadRequirementDocument: (projectId: number, documentId: number) => Promise<Blob>;
  fetchRequirementJobs: (projectId: number) => Promise<RequirementGenerationJob[]>;
  queueRequirementGeneration: (
    projectId: number,
    payload: QueueRequirementGenerationPayload,
  ) => Promise<RequirementGenerationJob>;
  fetchRequirementGeneration: (
    projectId: number,
    generationId: number,
  ) => Promise<RequirementGenerationJob>;
  downloadRequirementArtifact: (
    projectId: number,
    generationId: number,
    artifactId: number,
    fallbackFilename?: string,
  ) => Promise<RequirementService.DownloadedArtifactFile>;
  fetchDomainKnowledge: (projectId: number) => Promise<DomainKnowledge | null>;
  refreshDomainKnowledge: (
    projectId: number,
    options?: { documentIds?: number[] },
  ) => Promise<DomainKnowledge>;
  fetchEnterpriseKnowledge: (projectId: number) => Promise<EnterpriseKnowledge | null>;
  refreshEnterpriseKnowledge: (
    projectId: number,
    options?: { documentIds?: number[] },
  ) => Promise<EnterpriseKnowledge>;
  saveEnterpriseKnowledge: (
    projectId: number,
    payload: EnterpriseKnowledgePayload,
  ) => Promise<EnterpriseKnowledge>;
}

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const selectedProjectId = state.selectedProject?.id ?? null;

  const setErrorMessage = useCallback((message: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: message });
  }, []);

  const withLoader = useCallback(async (fn: () => Promise<void>) => {
    dispatch({ type: 'SET_LOADING', payload: true });
    setErrorMessage(null);
    try {
      await fn();
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [setErrorMessage]);

  const setDomainKnowledgeState = useCallback((projectId: number, knowledge: DomainKnowledge | null) => {
    dispatch({ type: 'SET_DOMAIN_KNOWLEDGE', payload: { projectId, knowledge } });
  }, []);

  const setEnterpriseKnowledgeState = useCallback((projectId: number, knowledge: EnterpriseKnowledge | null) => {
    dispatch({ type: 'SET_ENTERPRISE_KNOWLEDGE', payload: { projectId, knowledge } });
  }, []);

  const refreshProjects = useCallback(async () => {
    await withLoader(async () => {
      const projects = await ProjectService.fetchProjects();
      dispatch({ type: 'SET_PROJECTS', payload: projects });
      projects.forEach(project => {
        if (project.domain_knowledge !== undefined) {
          setDomainKnowledgeState(project.id, project.domain_knowledge ?? null);
        }
        if (project.enterprise_knowledge !== undefined) {
          setEnterpriseKnowledgeState(project.id, project.enterprise_knowledge ?? null);
        }
      });
      if (selectedProjectId != null) {
        const selected = projects.find(p => p.id === selectedProjectId) || null;
        dispatch({ type: 'SET_SELECTED', payload: selected });
      }
    });
  }, [selectedProjectId, setDomainKnowledgeState, setEnterpriseKnowledgeState, withLoader]);

  const refreshProject = useCallback(
    async (projectId: number) => {
      let project: ProjectWithRepository | null = null;
      await withLoader(async () => {
        project = await ProjectService.fetchProject(projectId);
        dispatch({ type: 'UPSERT_PROJECT', payload: project! });
        if (project && project.domain_knowledge !== undefined) {
          setDomainKnowledgeState(project.id, project.domain_knowledge ?? null);
        }
        if (project && project.enterprise_knowledge !== undefined) {
          setEnterpriseKnowledgeState(project.id, project.enterprise_knowledge ?? null);
        }
      });
      return project;
    },
    [setDomainKnowledgeState, setEnterpriseKnowledgeState, withLoader],
  );

  const createProject = useCallback(
    async (payload: CreateProjectPayload) => {
      let created: ProjectWithRepository | null = null;
      await withLoader(async () => {
        created = await ProjectService.createProject(payload);
        dispatch({ type: 'UPSERT_PROJECT', payload: created });
        if (created && created.domain_knowledge !== undefined) {
          setDomainKnowledgeState(created.id, created.domain_knowledge ?? null);
        }
        if (created && created.enterprise_knowledge !== undefined) {
          setEnterpriseKnowledgeState(created.id, created.enterprise_knowledge ?? null);
        }
      });
      return created!;
    },
    [setDomainKnowledgeState, setEnterpriseKnowledgeState, withLoader],
  );

  const createProjectWithKnowledge = useCallback(
    async (
      payload: CreateProjectPayload,
      options?: CreateProjectWithKnowledgeOptions,
    ) => {
      let created: ProjectWithRepository | null = null;
      await withLoader(async () => {
        created = await ProjectService.createProjectWithKnowledge(payload, options);
        dispatch({ type: 'UPSERT_PROJECT', payload: created });
        if (created && created.domain_knowledge !== undefined) {
          setDomainKnowledgeState(created.id, created.domain_knowledge ?? null);
        }
        if (created && created.enterprise_knowledge !== undefined) {
          setEnterpriseKnowledgeState(created.id, created.enterprise_knowledge ?? null);
        }
      });
      return created!;
    },
    [setDomainKnowledgeState, setEnterpriseKnowledgeState, withLoader],
  );

  const updateProject = useCallback(
    async (projectId: number, payload: UpdateProjectPayload) => {
      let updated: ProjectWithRepository | null = null;
      await withLoader(async () => {
        updated = await ProjectService.updateProject(projectId, payload);
        dispatch({ type: 'UPSERT_PROJECT', payload: updated });
        if (updated && updated.domain_knowledge !== undefined) {
          setDomainKnowledgeState(updated.id, updated.domain_knowledge ?? null);
        }
        if (updated && updated.enterprise_knowledge !== undefined) {
          setEnterpriseKnowledgeState(updated.id, updated.enterprise_knowledge ?? null);
        }
      });
      return updated!;
    },
    [setDomainKnowledgeState, setEnterpriseKnowledgeState, withLoader],
  );

  const deleteProjectHandler = useCallback(
    async (projectId: number) => {
      await withLoader(async () => {
        await ProjectService.deleteProject(projectId);
        dispatch({ type: 'REMOVE_PROJECT', payload: projectId });
      });
    },
    [withLoader],
  );

  const uploadRepository = useCallback(
    async (
      projectId: number,
      file: File,
      metadata?: { name?: string; description?: string; azureRepoName?: string; azureBranch?: string },
    ) => {
      let repository: RepositorySummary | null = null;
      await withLoader(async () => {
        await ProjectService.uploadProjectRepository(projectId, file, metadata);
        repository = await ProjectService.fetchProjectRepository(projectId);
        const project = await ProjectService.fetchProject(projectId);
        dispatch({ type: 'UPSERT_PROJECT', payload: project });
      });
      return repository!;
    },
    [withLoader],
  );

  const integrateAzureRepo = useCallback(
    async (
      projectId: number,
      payload: AzureRepoIntegrationPayload,
    ) => {
      let repository: RepositorySummary | null = null;
      await withLoader(async () => {
        await ProjectService.integrateAzureRepo(projectId, payload);
        repository = await ProjectService.fetchProjectRepository(projectId);
        const project = await ProjectService.fetchProject(projectId);
        dispatch({ type: 'UPSERT_PROJECT', payload: project });
      });
      return repository!;
    },
    [withLoader],
  );

  const removeRepository = useCallback(
    async (projectId: number) => {
      await withLoader(async () => {
        await ProjectService.deleteProjectRepository(projectId);
        const project = await ProjectService.fetchProject(projectId);
        dispatch({ type: 'UPSERT_PROJECT', payload: project });
      });
    },
    [withLoader],
  );

  const fetchRequirementDocuments = useCallback(async (projectId: number) => {
    setErrorMessage(null);
    try {
      const documents = await RequirementService.listRequirementDocuments(projectId);
      dispatch({ type: 'SET_REQUIREMENT_DOCUMENTS', payload: { projectId, documents } });
      return documents;
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      throw error;
    }
  }, [setErrorMessage]);

  const uploadRequirementDocuments = useCallback(async (
    projectId: number,
    files: File[],
    options?: { notes?: string; classification?: import('../types/requirements').DocumentClassification },
  ) => {
    let documents: RequirementDocument[] = [];
    await withLoader(async () => {
      await RequirementService.uploadRequirementDocuments(projectId, files, options);
      documents = await RequirementService.listRequirementDocuments(projectId);
      dispatch({ type: 'SET_REQUIREMENT_DOCUMENTS', payload: { projectId, documents } });
    });
    return documents;
  }, [withLoader]);

  const deleteRequirementDocument = useCallback(async (projectId: number, documentId: number) => {
    await withLoader(async () => {
      await RequirementService.deleteRequirementDocument(projectId, documentId);
      const documents = await RequirementService.listRequirementDocuments(projectId);
      dispatch({ type: 'SET_REQUIREMENT_DOCUMENTS', payload: { projectId, documents } });
    });
  }, [withLoader]);

  const reprocessRequirementDocument = useCallback(async (projectId: number, documentId: number) => {
    await withLoader(async () => {
      await RequirementService.reprocessRequirementDocument(projectId, documentId);
    });
    const docs = await fetchRequirementDocuments(projectId);
    const updated = docs.find(doc => doc.id === documentId);
    if (!updated) {
      throw new Error('Requirement document not found after reprocess');
    }
    return updated;
  }, [fetchRequirementDocuments, withLoader]);

  const downloadRequirementDocument = useCallback(async (projectId: number, documentId: number) => {
    setErrorMessage(null);
    try {
      return await RequirementService.downloadRequirementDocument(projectId, documentId);
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      throw error;
    }
  }, [setErrorMessage]);

  const fetchRequirementJobs = useCallback(async (projectId: number) => {
    setErrorMessage(null);
    try {
      const jobs = await RequirementService.listRequirementGenerations(projectId);
      dispatch({ type: 'SET_REQUIREMENT_JOBS', payload: { projectId, jobs } });
      return jobs;
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      throw error;
    }
  }, [setErrorMessage]);

  const queueRequirementGeneration = useCallback(async (
    projectId: number,
    payload: QueueRequirementGenerationPayload,
  ) => {
    let job: RequirementGenerationJob | null = null;
    await withLoader(async () => {
      job = await RequirementService.queueRequirementGeneration(projectId, payload);
      const jobs = await RequirementService.listRequirementGenerations(projectId);
      dispatch({ type: 'SET_REQUIREMENT_JOBS', payload: { projectId, jobs } });
    });
    return job!;
  }, [withLoader]);

  const fetchRequirementGeneration = useCallback(async (
    projectId: number,
    generationId: number,
  ) => {
    setErrorMessage(null);
    try {
      const job = await RequirementService.fetchRequirementGeneration(projectId, generationId);
      await fetchRequirementJobs(projectId);
      return job;
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      throw error;
    }
  }, [fetchRequirementJobs, setErrorMessage]);

  const downloadRequirementArtifact = useCallback(async (
    projectId: number,
    generationId: number,
    artifactId: number,
    fallbackFilename?: string,
  ) => {
    setErrorMessage(null);
    try {
      return await RequirementService.downloadRequirementArtifact(
        projectId,
        generationId,
        artifactId,
        fallbackFilename,
      );
    } catch (error: any) {
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      throw error;
    }
  }, [setErrorMessage]);


  const fetchDomainKnowledge = useCallback(async (projectId: number) => {
    try {
      const knowledge = await ProjectService.fetchProjectDomainKnowledge(projectId);
      setDomainKnowledgeState(projectId, knowledge);
      return knowledge;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        setDomainKnowledgeState(projectId, null);
        return null;
      }
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      throw error;
    }
  }, [setDomainKnowledgeState, setErrorMessage]);

  const refreshDomainKnowledge = useCallback(async (
    projectId: number,
    options?: { documentIds?: number[] },
  ) => {
    let knowledge: DomainKnowledge | null = null;
    try {
      const payload = options && Array.isArray(options.documentIds)
        ? { document_ids: options.documentIds }
        : undefined;
      knowledge = await ProjectService.refreshProjectDomainKnowledge(projectId, payload);
      setDomainKnowledgeState(projectId, knowledge);
    } catch (error) {
      throw error;
    }
    return knowledge!;
  }, [setDomainKnowledgeState]);

  const fetchEnterpriseKnowledge = useCallback(async (projectId: number) => {
    try {
      const knowledge = await ProjectService.fetchProjectEnterpriseKnowledge(projectId);
      setEnterpriseKnowledgeState(projectId, knowledge);
      return knowledge;
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 404) {
        setEnterpriseKnowledgeState(projectId, null);
        return null;
      }
      const message = extractErrorMessage(error);
      setErrorMessage(message);
      throw error;
    }
  }, [setEnterpriseKnowledgeState, setErrorMessage]);

  const refreshEnterpriseKnowledge = useCallback(async (
    projectId: number,
    options?: { documentIds?: number[] },
  ) => {
    let knowledge: EnterpriseKnowledge | null = null;
    try {
      const payload = options && Array.isArray(options.documentIds)
        ? { document_ids: options.documentIds }
        : undefined;
      knowledge = await ProjectService.refreshProjectEnterpriseKnowledge(projectId, payload);
      setEnterpriseKnowledgeState(projectId, knowledge);
    } catch (error) {
      throw error;
    }
    return knowledge!;
  }, [setEnterpriseKnowledgeState]);

  const saveEnterpriseKnowledge = useCallback(async (
    projectId: number,
    payload: EnterpriseKnowledgePayload,
  ) => {
    let knowledge: EnterpriseKnowledge | null = null;
    dispatch({ type: 'SET_LOADING', payload: true });
    try {
      knowledge = await ProjectService.saveProjectEnterpriseKnowledge(projectId, payload);
      setEnterpriseKnowledgeState(projectId, knowledge);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
    return knowledge!;
  }, [setEnterpriseKnowledgeState]);

  const value = useMemo<ProjectContextValue>(() => ({
    projects: state.projects,
    selectedProject: state.selectedProject,
    loading: state.loading,
    error: state.error,
    requirementDocuments: state.requirementDocuments,
    requirementJobs: state.requirementJobs,
    domainKnowledge: state.domainKnowledge,
    enterpriseKnowledge: state.enterpriseKnowledge,
    refreshProjects,
    refreshProject,
    createProject,
    createProjectWithKnowledge,
    updateProject,
    deleteProject: deleteProjectHandler,
    uploadRepository,
    integrateAzureRepo,
    removeRepository,
    fetchRequirementDocuments,
    uploadRequirementDocuments,
    deleteRequirementDocument,
    reprocessRequirementDocument,
    downloadRequirementDocument,
    fetchRequirementJobs,
    queueRequirementGeneration,
    fetchRequirementGeneration,
    downloadRequirementArtifact,
    fetchDomainKnowledge,
    refreshDomainKnowledge,
    fetchEnterpriseKnowledge,
    refreshEnterpriseKnowledge,
    saveEnterpriseKnowledge,
  }), [
    state,
    refreshProjects,
    refreshProject,
    createProject,
    createProjectWithKnowledge,
    updateProject,
    deleteProjectHandler,
    uploadRepository,
    integrateAzureRepo,
    removeRepository,
    fetchRequirementDocuments,
    uploadRequirementDocuments,
    deleteRequirementDocument,
    reprocessRequirementDocument,
    downloadRequirementDocument,
    fetchRequirementJobs,
    queueRequirementGeneration,
    fetchRequirementGeneration,
    downloadRequirementArtifact,
    fetchDomainKnowledge,
    refreshDomainKnowledge,
    fetchEnterpriseKnowledge,
    refreshEnterpriseKnowledge,
    saveEnterpriseKnowledge,
  ]);

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export const useProjects = (): ProjectContextValue => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectProvider');
  }
  return context;
};
